/**
 * Log local em memória (F20-T02).
 *
 * O app roda offline e sem canal de suporte: quando algo falha, o único jeito de o
 * usuário reportar é copiar um retrato do que aconteceu. Fica em memória de
 * propósito — persistir logs no IndexedDB competiria por espaço com os vídeos, que
 * é justamente o recurso escasso.
 */
export type LogLevel = 'info' | 'warn' | 'error';

export interface LogEntry {
  at: string;
  level: LogLevel;
  scope: string;
  message: string;
}

const MAX_ENTRIES = 200;

const entries: LogEntry[] = [];
const listeners = new Set<() => void>();

function push(level: LogLevel, scope: string, message: string): void {
  entries.push({ at: new Date().toISOString(), level, scope, message });

  // Buffer circular: um erro em laço não pode consumir memória sem limite.
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);

  for (const listener of listeners) listener();
}

function describe(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export const logger = {
  info(scope: string, message: string): void {
    push('info', scope, message);
  },

  warn(scope: string, message: string, cause?: unknown): void {
    push('warn', scope, cause ? `${message} — ${describe(cause)}` : message);
    console.warn(`[${scope}] ${message}`, cause ?? '');
  },

  error(scope: string, message: string, cause?: unknown): void {
    push('error', scope, cause ? `${message} — ${describe(cause)}` : message);
    console.error(`[${scope}] ${message}`, cause ?? '');
  },

  list(): LogEntry[] {
    return [...entries];
  },

  clear(): void {
    entries.length = 0;
    for (const listener of listeners) listener();
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/**
 * Captura erros não tratados da janela.
 *
 * Chamado uma vez no boot. Sem isso, uma promessa rejeitada em algum caminho
 * fire-and-forget desapareceria sem deixar rastro para o diagnóstico.
 */
export function installGlobalErrorLogging(): () => void {
  const onError = (event: ErrorEvent): void => {
    logger.error('window', event.message);
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    logger.error('promise', describe(event.reason));
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
